import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import PushPinRoundedIcon from "@mui/icons-material/PushPinRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";

import {
  changeLegalFileStage,
  releaseLegalFileStage,
} from "../../../services/LegalFileService";
import { getLegalFileStages } from "../../../services/LegalFileStageService";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { colorForStage } from "../../../config/legalFileStages";
import { isLegalFileLocked, LOCKED_LEGAL_FILE_MESSAGE } from "./detailHelpers";

function StageDot({ color, size = 9 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: color,
        flexShrink: 0,
      }}
    />
  );
}

export default function LegalFileStageChip({ legalFile, onRefresh }) {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();

  const locked = isLegalFileLocked(legalFile);
  const canChange = hasPermission("cambiar etapa expediente") && !locked;
  const currentStage = legalFile.stage;
  const currentColor = colorForStage(currentStage);

  // 'forced' = forzada por importación: congelada.
  // 'manual' = fijada a mano: es un piso, el automático solo la avanza,
  //            nunca la lleva a una etapa anterior.
  const stagePinned = Boolean(legalFile.stage_source);
  const pinnedLabel =
    legalFile.stage_source === "forced"
      ? "Etapa forzada por importación — el recálculo no la cambia"
      : "Etapa fijada a mano — sólo puede avanzar, el recálculo no la retrocede";

  const [anchorEl, setAnchorEl] = useState(null);
  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleOpen = async (event) => {
    if (!canChange || saving) return;

    setAnchorEl(event.currentTarget);

    if (stages.length === 0) {
      setLoadingStages(true);
      try {
        const res = await getLegalFileStages();
        setStages(
          (res.data?.data || []).slice().sort((a, b) => a.order - b.order),
        );
      } catch {
        showToast("No se pudieron cargar las etapas.", "error");
      } finally {
        setLoadingStages(false);
      }
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = async (stage) => {
    if (stage.id === currentStage?.id && !stagePinned) {
      handleClose();
      return;
    }

    setSaving(true);
    try {
      await changeLegalFileStage(legalFile.id, stage.id);
      showToast(`Etapa fijada en "${stage.name}".`, "success");
      handleClose();
      await onRefresh();
    } catch (err) {
      showToast(
        err.response?.data?.message || "No se pudo actualizar la etapa.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRelease = async () => {
    setSaving(true);
    try {
      await releaseLegalFileStage(legalFile.id);
      showToast("La etapa volvió a modo automático.", "success");
      handleClose();
      await onRefresh();
    } catch (err) {
      showToast(
        err.response?.data?.message || "No se pudo volver a modo automático.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const pillSx = (theme) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 0.9,
    height: 34,
    px: 1.4,
    borderRadius: 999,
    fontWeight: 800,
    fontSize: 13,
    lineHeight: 1,
    textTransform: "none",
    letterSpacing: 0.2,
    color: "text.primary",
    bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#fff",
    border: "1px solid",
    borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.14)" : "grey.300",
  });

  if (!canChange) {
    return (
      <Tooltip
        title={
          locked
            ? LOCKED_LEGAL_FILE_MESSAGE
            : stagePinned
              ? pinnedLabel
              : "Etapa del expediente"
        }
      >
        <Box sx={pillSx}>
          <StageDot color={currentColor} />
          <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
            {currentStage?.name || "SIN ETAPA"}
          </Typography>
          {stagePinned && (
            <PushPinRoundedIcon sx={{ fontSize: 14, opacity: 0.6 }} />
          )}
        </Box>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip
        title={
          stagePinned
            ? pinnedLabel
            : "Etapa del expediente — click para cambiarla"
        }
      >
        <Button
          onClick={handleOpen}
          disabled={saving}
          sx={(theme) => ({
            ...pillSx(theme),
            "&:hover": {
              bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "grey.50",
              borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.22)" : "grey.400",
            },
          })}
        >
          {saving ? (
            <CircularProgress size={13} />
          ) : (
            <StageDot color={currentColor} />
          )}
          {currentStage?.name || "SIN ETAPA"}
          {stagePinned && (
            <PushPinRoundedIcon sx={{ fontSize: 14, opacity: 0.6, ml: -0.2 }} />
          )}
          <ExpandMoreRoundedIcon
            sx={{
              fontSize: 18,
              ml: -0.3,
              opacity: 0.6,
              transform: anchorEl ? "rotate(180deg)" : "none",
              transition: "transform .15s ease",
            }}
          />
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 260,
              borderRadius: 3,
              py: 0.75,
              boxShadow: "0 16px 40px rgba(0,0,0,0.16)",
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 0.75, display: "flex", alignItems: "center", gap: 0.8 }}>
          <TimelineRoundedIcon sx={{ fontSize: 16, opacity: 0.55 }} />
          <Typography sx={{ fontSize: 11.5, fontWeight: 800, opacity: 0.55, letterSpacing: 0.4 }}>
            ETAPA DEL EXPEDIENTE
          </Typography>
        </Box>

        {loadingStages && (
          <MenuItem disabled sx={{ py: 1.2, gap: 1.2 }}>
            <CircularProgress size={16} />
            Cargando etapas…
          </MenuItem>
        )}

        {!loadingStages &&
          stages.map((stage) => {
            const isSelected = stage.id === currentStage?.id;
            return (
              <MenuItem
                key={stage.id}
                selected={isSelected}
                disabled={saving}
                onClick={() => handleSelect(stage)}
                sx={{
                  py: 1,
                  mx: 0.75,
                  my: 0.2,
                  borderRadius: 2,
                  gap: 1.2,
                  "&.Mui-selected": {
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "grey.100",
                  },
                }}
              >
                <StageDot color={colorForStage(stage)} size={10} />
                <Typography sx={{ flex: 1, fontWeight: isSelected ? 900 : 700, fontSize: 13.5 }}>
                  {stage.name}
                </Typography>
                {isSelected && (
                  <CheckRoundedIcon sx={{ fontSize: 18, color: colorForStage(stage) }} />
                )}
              </MenuItem>
            );
          })}

        {stagePinned && !loadingStages && (
          <Box>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              disabled={saving}
              onClick={handleRelease}
              sx={{ py: 1, mx: 0.75, my: 0.2, borderRadius: 2, gap: 1.2 }}
            >
              <AutorenewRoundedIcon sx={{ fontSize: 18, opacity: 0.7 }} />
              <Typography sx={{ flex: 1, fontWeight: 700, fontSize: 13.5 }}>
                Volver a etapa automática
              </Typography>
            </MenuItem>
          </Box>
        )}
      </Menu>
    </>
  );
}
